/* eslint-disable max-lines */
import {
  BottomSheetWithIntent,
  CbhIcon,
  Divider,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { TimePicker } from "@src/appV2/redesign/components/TimePicker";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { isBefore, startOfMinute, subMinutes } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../../components/Button";
import { DialogFooter } from "../../../components/DialogFooter";
import {
  type ClockInErrorType,
  type ClockInGeofenceValidationType,
  validateClockInTimestamp,
} from "../../MissedPunches/utils/validateClockInTimestamp";
import { isBoundaryError } from "../analytics/isBoundaryError";
import { getBottomSheetIntent } from "./utils/getBottomSheetIntent";
import { getDayIndicator } from "./utils/getDayIndicator";
import { inferDateFromTime } from "./utils/inferDateFromTime";
import { getLocalTimeForPicker, getUtcTimeFromPicker } from "./utils/timezoneConversion";

interface EditClockInTimeFormValues {
  clockInTime?: Date;
}

interface EditClockInTimeBottomSheetProps {
  modalState: UseModalState;
  clockInTime?: Date;
  workplaceTimezone: string;
  shiftStartAt: Date;
  shiftEndAt: Date;
  isNfcShift: boolean;
  earliestGeofenceEntryTime?: Date;
  earliestPlausibleArrivalTime?: Date;
  clockInButtonTappedAt?: Date;
  earlyClockInEnabled: boolean;
  earlyClockInEnabledInMinutes?: number;
  clockOutTime?: Date;
  breakStartTime?: Date;
  workerClockIn?: Date;
  isGeofenceFacility?: boolean;
  isMissedPunchRequestEnabled: boolean;
  isEnforceGeofenceTimestampsEnabled?: boolean;
  verifiedClockIn?: Date;
  shiftId?: string;
  workplaceId?: string;
  onConfirm: (clockInTime: Date) => void;
  onCancel?: () => void;
}

const GEOFENCE_ENTRY_TIME_BUFFER_IN_MINUTES = 5;

export function EditClockInTimeBottomSheet(props: EditClockInTimeBottomSheetProps) {
  const {
    modalState,
    clockInTime,
    workplaceTimezone,
    shiftStartAt,
    shiftEndAt,
    isNfcShift,
    earliestGeofenceEntryTime,
    earliestPlausibleArrivalTime,
    clockInButtonTappedAt,
    earlyClockInEnabled,
    earlyClockInEnabledInMinutes,
    clockOutTime,
    breakStartTime,
    workerClockIn,
    isGeofenceFacility = false,
    isMissedPunchRequestEnabled,
    isEnforceGeofenceTimestampsEnabled = false,
    verifiedClockIn,
    shiftId,
    workplaceId,
    onConfirm,
    onCancel,
  } = props;

  const [saveAttempted, setSaveAttempted] = useState(false);

  const { setValue, watch, reset } = useForm<EditClockInTimeFormValues>({
    defaultValues: {
      clockInTime: isDefined(clockInTime)
        ? getLocalTimeForPicker(clockInTime, workplaceTimezone)
        : undefined,
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      const resetValue = isDefined(clockInTime)
        ? getLocalTimeForPicker(clockInTime, workplaceTimezone)
        : undefined;
      reset({
        clockInTime: resetValue,
      });
      setSaveAttempted(false);
    }
  }, [modalState.modalIsOpen, clockInTime, reset, workplaceTimezone]);

  const enforceNfcTimesheetBounds =
    useCbhFlag(CbhFeatureFlag.ENFORCE_NFC_TIMESHEET_BOUNDS, {
      defaultValue: false,
    }) && isNfcShift;

  const currentLocalClockInTime = watch("clockInTime");

  // Convert back to UTC for validation and submission
  const currentClockInTime = isDefined(currentLocalClockInTime)
    ? getUtcTimeFromPicker(currentLocalClockInTime, workplaceTimezone)
    : undefined;

  const trueShiftStartTime = earlyClockInEnabled
    ? subMinutes(shiftStartAt, earlyClockInEnabledInMinutes ?? 0)
    : shiftStartAt;

  // Calculate "Earliest allowable clock in time" for regular shifts, "Latest" for NFC shifts
  const earliestAllowableClockInTime = useMemo(() => {
    const earliestGeofenceEntryTimeWithBuffer = isDefined(earliestGeofenceEntryTime)
      ? subMinutes(earliestGeofenceEntryTime, GEOFENCE_ENTRY_TIME_BUFFER_IN_MINUTES)
      : undefined;
    const times = [clockInButtonTappedAt, earliestGeofenceEntryTimeWithBuffer]
      .filter(isDefined)
      .map((t) => t.getTime());
    if (times.length === 0) {
      return undefined;
    }

    return startOfMinute(
      new Date(enforceNfcTimesheetBounds ? Math.max(...times) : Math.min(...times))
    );
  }, [clockInButtonTappedAt, earliestGeofenceEntryTime, enforceNfcTimesheetBounds]);

  const getStandardValidationError = (timeToValidate: Date): string | undefined => {
    // Check against earliest allowable clock in time (geofence-based)
    if (
      isDefined(earliestAllowableClockInTime) &&
      isBefore(trueShiftStartTime, earliestAllowableClockInTime) &&
      isBefore(timeToValidate, earliestAllowableClockInTime)
    ) {
      return `You cannot clock in prior to your recorded arrival time at ${formatTimeWithTimezone(
        earliestAllowableClockInTime,
        workplaceTimezone,
        false
      )}.`;
    }

    // Check against shift start time
    if (
      isDefined(earliestAllowableClockInTime) &&
      isBefore(earliestAllowableClockInTime, trueShiftStartTime) &&
      isBefore(timeToValidate, trueShiftStartTime)
    ) {
      return `Earliest permissible clock in time is ${formatTimeWithTimezone(
        trueShiftStartTime,
        workplaceTimezone,
        false
      )}`;
    }

    // Check against clock out time
    if (isDefined(clockOutTime) && !isBefore(timeToValidate, clockOutTime)) {
      return "You cannot clock in after your clock out time.";
    }

    // Check against break start time
    if (isDefined(breakStartTime) && !isBefore(timeToValidate, breakStartTime)) {
      return "You cannot clock in after your break start time.";
    }

    return undefined;
  };

  const getValidationMessages = (
    selectedTime?: Date
  ): {
    errorMessage?: string;
    warningMessage?: string;
    infoMessage?: string;
    errorType?: ClockInErrorType;
    geofenceValidationType?: ClockInGeofenceValidationType;
  } => {
    if (!isDefined(selectedTime)) {
      return {};
    }

    // Use centralized validation when feature flag is enabled
    if (isMissedPunchRequestEnabled) {
      const validationResult = validateClockInTimestamp({
        selectedTime,
        geofenceArrivalTime: earliestGeofenceEntryTime,
        earliestPlausibleArrivalTime,
        shiftStartAt,
        facilitySettings: {
          preventEarlyClockIns: !earlyClockInEnabled,
          earlyClockInMinutes: earlyClockInEnabledInMinutes,
        },
        workerClockIn,
        clockOutTime,
        breakStartTime,
        isGeofenceFacility,
        isNfcOrMeshFacility: isNfcShift,
        isEnforceGeofenceTimestampsEnabled,
        verifiedClockIn,
        workplaceTimezone,
      });

      // Return error message if validation failed
      if (!validationResult.isValid) {
        return {
          errorMessage: validationResult.message,
          errorType: validationResult.errorType,
          geofenceValidationType: validationResult.geofenceValidationType,
        };
      }

      // For the current phase of the missed punch rollout, we deliberately
      // suppress the non-blocking "You are recording a missed punch" banner.
      // The underlying validation still returns a MISSED_PUNCH_WARNING flag,
      // but we do not surface a message in this flow.

      //  if (validationResult.warningType === "MISSED_PUNCH_WARNING") {
      //   return { infoMessage: validationResult.message };
      // }

      return {};
    }

    // Legacy validation (when feature flag is off)
    const legacyError = getStandardValidationError(selectedTime);
    if (legacyError) {
      return { errorMessage: legacyError };
    }

    return {};
  };

  const { errorMessage, warningMessage, infoMessage, errorType, geofenceValidationType } =
    getValidationMessages(currentClockInTime);

  const handleConfirm = () => {
    setSaveAttempted(true);

    // Track boundary violations when user attempts to save with an error
    if (isBoundaryError(errorType)) {
      logEvent(APP_V2_USER_EVENTS.MISSED_PUNCH_REQUEST_TIMESHEET_BOUNDARY_VIOLATION_ATTEMPTED, {
        actor: "worker",
        clockAction: "clock-in",
        errorType,
        shiftId,
        workplaceId,
        selectedTime: currentClockInTime?.toISOString(),
        geofenceArrivalTime: earliestGeofenceEntryTime?.toISOString(),
        shiftStartAt: shiftStartAt.toISOString(),
        verifiedClockIn: verifiedClockIn?.toISOString(),
        earliestPlausibleArrivalTime: earliestPlausibleArrivalTime?.toISOString(),
        geofenceValidationType,
      });
    }

    if (isDefined(currentClockInTime) && !errorMessage) {
      onConfirm(currentClockInTime);
      modalState.closeModal();
    }
  };

  const handleCancel = () => {
    onCancel?.();
    modalState.closeModal();
  };

  const handleTimeChange = (newTime: Date | undefined) => {
    if (isDefined(newTime)) {
      // Infer the correct date based on the shift window
      const inferResult = inferDateFromTime({
        timeInput: { hour: newTime.getHours(), minute: newTime.getMinutes() },
        shiftStart: getLocalTimeForPicker(shiftStartAt, workplaceTimezone),
        shiftEnd: getLocalTimeForPicker(shiftEndAt, workplaceTimezone),
        earlyBufferMinutes: earlyClockInEnabledInMinutes ?? 30,
      });

      // When success is false, time is outside valid window - still set it but validation will catch it
      const timeToSet = inferResult.success ? inferResult.date : newTime;
      setValue("clockInTime", timeToSet, { shouldDirty: true });

      setSaveAttempted(false);
    }
  };

  const isValid = isDefined(currentClockInTime);
  const dayIndicator = getDayIndicator({
    selectedTimestamp: currentClockInTime,
    referenceTimestamp: shiftStartAt,
    workplaceTimezone,
  });

  const { intent, intentContent, intentIcon } = getBottomSheetIntent({
    saveAttempted,
    errorMessage,
    warningMessage,
    infoMessage,
  });

  return (
    <BottomSheetWithIntent
      modalState={modalState}
      intent={intent}
      intentContent={intentContent}
      intentIcon={intentIcon}
      footer={
        <DialogFooter orientation="vertical" onClose={handleCancel}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isValid}
            onClick={handleConfirm}
          >
            Save
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={6}>
          <Title semibold variant="h4" component="h2">
            Edit time
          </Title>

          <Stack spacing={4}>
            <Stack direction="row" spacing={3} alignItems="center">
              <CbhIcon size="medium" type="clock" />
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexGrow: 1 }}>
                <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
                  Clock in
                </Text>
                {dayIndicator && (
                  <Text sx={{ color: (theme) => theme.palette.text.disabled }}>{dayIndicator}</Text>
                )}
              </Stack>
              <Box textAlign="right">
                <Button aria-label="Select clock in time" variant="outlined" size="small">
                  {isDefined(currentClockInTime)
                    ? formatTimeWithTimezone(currentClockInTime, workplaceTimezone, false)
                    : "--:-- --"}
                </Button>
              </Box>
            </Stack>

            <Divider />
            <TimePicker
              preventParentTouchStart
              ampm
              skipDisabled
              value={currentLocalClockInTime}
              timeSteps={{ minutes: 1 }}
              containerSx={{
                // make sure the time picker takes as little horizontal space as possible
                // this preserves more area on the left/right for the bottom sheet to capture touch events
                // for swipe-down closing (dismiss)
                alignSelf: "center",
              }}
              sx={{
                justifyContent: "center",
                "& .MuiList-root": {
                  scrollbarWidth: "none",
                },
              }}
              onChange={handleTimeChange}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheetWithIntent>
  );
}
/* eslint-enable max-lines */
